import { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { TerritoryShowcaseItem } from '@buzzmap/shared';
import { categoryColors, formatDateRange } from '../../utils';
import { illustrationImage } from '../../utils/brandImages';
import { useDeferredPhotoUrl } from '../../utils/deferredPhoto';
import { EventLink } from '../events/EventLink';
import styles from './TerritoryShowcase.module.css';

// Silhouette senza foto: 56 px (TerritoryShowcase.module.css .illustration).
const SILHOUETTE_SIZES = '56px';

interface TerritoryShowcaseProps {
  items: TerritoryShowcaseItem[];
  /**
   * Drop the surrounding card surface (padding, background, shadow) so the
   * strip sits straight on the page background. Set by the Esplora desktop
   * list column, where it is the first block; the home bands and the mobile
   * Esplora keep the surface (white on the blue band, there).
   */
  bare?: boolean;
}

function ShowcaseCover({ item }: { item: TerritoryShowcaseItem }) {
  const [failed, setFailed] = useState(false);
  const imageUrl = item.imageUrl?.startsWith('https://') ? item.imageUrl : undefined;
  // T204-INT: no download while the server shell still hides the app (see deferredPhoto).
  const startableUrl = useDeferredPhotoUrl(imageUrl);

  if (!imageUrl || failed) {
    // No usable photo: the category colour plus the same white category
    // silhouette the cards use. aria-hidden keeps it out of the a11y tree,
    // so assistive tech only ever sees the title link.
    return (
      <div className={styles.categoryTile} aria-hidden="true">
        <img
          className={styles.illustration}
          {...illustrationImage(item.category, SILHOUETTE_SIZES)}
          alt=""
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <img
      className={styles.image}
      src={startableUrl}
      alt={item.title}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}

/** Server-selected items are already in ItemList order; never sort or fetch here. */
export function TerritoryShowcase({ items, bare = false }: TerritoryShowcaseProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const trackRef = useRef<HTMLDivElement>(null);

  if (items.length === 0) return null;

  // Arrows page the track sideways; two screens' worth would jump past the
  // end on short strips, so a fixed fraction of what is visible instead.
  const scrollTrack = (direction: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({
      left: direction * Math.round(track.clientWidth * 0.8),
      behavior: reduced ? 'auto' : 'smooth',
    });
  };

  return (
    <section
      className={bare ? `${styles.section} ${styles.bare}` : styles.section}
      aria-labelledby="territory-showcase-title"
    >
      <div className={styles.headRow}>
        <h2 id="territory-showcase-title" className={styles.heading}>
          In evidenza
        </h2>
        <div className={styles.arrows}>
          <button
            type="button"
            className={styles.arrow}
            aria-label="Indietro"
            onClick={() => scrollTrack(-1)}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            type="button"
            className={styles.arrow}
            aria-label="Avanti"
            onClick={() => scrollTrack(1)}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      </div>
      <div className={styles.track} ref={trackRef} role="list" aria-label="Eventi in evidenza">
        {items.map((item) => {
          const place = item.venueName || item.city;
          return (
            <article
              key={item.id}
              className={styles.card}
              role="listitem"
              style={{ '--card-cat-color': categoryColors[item.category] } as React.CSSProperties}
            >
              <ShowcaseCover item={item} />
              <span className={styles.overlay} aria-hidden="true" />
              <div className={styles.details}>
                <h3 className={styles.title}>
                  <EventLink
                    eventId={item.id}
                    extended
                    onClick={() =>
                      navigate(`/event/${item.id}`, { state: { backgroundLocation: location } })
                    }
                  >
                    {item.title}
                  </EventLink>
                </h3>
                <p className={styles.meta}>
                  {`${formatDateRange(item.startDate, item.endDate)}${place ? ` · ${place}` : ''}`}
                </p>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
